import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Anchor, Lock, Save, Loader2, Users, BarChart3, ShieldAlert, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const FN_LABELS = { 1: "Function 1 — Navigation", 2: "Function 2 — Cargo Handling", 3: "Function 3 — Ship Ops & Safety" };

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      onLogin(data.token);
    } catch {
      toast.error("Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-sm border" data-testid="admin-login-card">
        <CardHeader className="text-center pb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Anchor className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>CalmOceanPrep</span>
          </div>
          <CardTitle className="text-base flex items-center justify-center gap-2">
            <Lock className="h-4 w-4" /> Admin Panel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-pw">Admin Password</Label>
              <Input
                id="admin-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoFocus
                data-testid="admin-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Access Admin Panel"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function StatsCard({ label, value, icon: Icon, color }) {
  return (
    <Card className="border">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold tabular-nums">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function PromptEditor({ fn, initialPrompt, adminToken }) {
  const [prompt, setPrompt] = useState(initialPrompt || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setPrompt(initialPrompt || ""); setDirty(false); }, [initialPrompt]);

  const handleSave = async () => {
    if (prompt.trim().length < 50) return toast.error("Prompt is too short (min 50 chars)");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/prompts", { function_number: fn, prompt }, {
        headers: { "X-Admin-Token": adminToken },
      });
      toast.success(data.message);
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save prompt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border" data-testid={`prompt-editor-${fn}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" /> {FN_LABELS[fn]}
          </span>
          {dirty && <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">Unsaved</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Textarea
          value={prompt}
          onChange={(e) => { setPrompt(e.target.value); setDirty(true); }}
          className="min-h-[200px] max-h-[400px] text-xs font-mono leading-relaxed"
          data-testid={`prompt-textarea-${fn}`}
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">{prompt.length.toLocaleString()} chars</span>
          <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid={`save-prompt-${fn}`}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem("cop_admin_token") || "");
  const [stats, setStats] = useState(null);
  const [prompts, setPrompts] = useState({});
  const [abuseLog, setAbuseLog] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLogin = (token) => {
    setAdminToken(token);
    sessionStorage.setItem("cop_admin_token", token);
  };

  const headers = { "X-Admin-Token": adminToken };

  const loadData = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const [statsRes, promptsRes, logsRes] = await Promise.all([
        api.get("/admin/stats", { headers }),
        api.get("/admin/prompts", { headers }),
        api.get("/admin/abuse-log", { headers }),
      ]);
      setStats(statsRes.data);
      setPrompts(promptsRes.data);
      setAbuseLog(logsRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        sessionStorage.removeItem("cop_admin_token");
        setAdminToken("");
        toast.error("Admin session expired. Please log in again.");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  if (!adminToken) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-panel">
      {/* Header */}
      <header className="cop-glass border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            <Anchor className="h-5 w-5 text-primary" />
            <span className="font-bold text-[#0a1628]" style={{ fontFamily: "Manrope" }}>Admin Panel</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadData} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => { sessionStorage.removeItem("cop_admin_token"); setAdminToken(""); }} data-testid="admin-logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 max-w-5xl py-6 space-y-8">
        {/* Stats Dashboard */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <BarChart3 className="h-5 w-5 text-primary" /> Dashboard
          </h2>
          {stats ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatsCard label="Total Users" value={stats.total_users} icon={Users} color="bg-teal-50 text-teal-700" />
              <StatsCard label="Sessions Today" value={stats.sessions_today} icon={BarChart3} color="bg-blue-50 text-blue-700" />
              <StatsCard label="Sessions All Time" value={stats.sessions_total} icon={BarChart3} color="bg-slate-100 text-slate-700" />
              <StatsCard label="Abuse Log Entries" value={stats.abuse_log_count} icon={ShieldAlert} color="bg-red-50 text-red-700" />
              <StatsCard label="Paid Users" value={stats.plans.weekly + stats.plans.monthly + stats.plans.all_access} icon={Users} color="bg-amber-50 text-amber-700" />
            </div>
          ) : (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          {stats && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge variant="secondary" className="text-xs">Free: {stats.plans.free}</Badge>
              <Badge variant="secondary" className="text-xs">Weekly: {stats.plans.weekly}</Badge>
              <Badge variant="secondary" className="text-xs">Monthly: {stats.plans.monthly}</Badge>
              <Badge variant="secondary" className="text-xs">All Access: {stats.plans.all_access}</Badge>
            </div>
          )}
        </section>

        <Separator />

        {/* System Prompts */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <FileText className="h-5 w-5 text-primary" /> System Prompts
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Edit the AI Surveyor's system prompt for each function. Changes take effect immediately for new sessions.
          </p>
          <div className="space-y-4">
            {[1, 2, 3].map((fn) => (
              <PromptEditor key={fn} fn={fn} initialPrompt={prompts[String(fn)]} adminToken={adminToken} />
            ))}
          </div>
        </section>

        <Separator />

        {/* Abuse Log */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <ShieldAlert className="h-5 w-5 text-red-600" /> Abuse Log (Latest 50)
          </h2>
          {abuseLog.length === 0 ? (
            <p className="text-sm text-muted-foreground">No abuse events recorded.</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="abuse-log-table">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-2 font-medium">Time</th>
                      <th className="text-left p-2 font-medium">Type</th>
                      <th className="text-left p-2 font-medium">User ID</th>
                      <th className="text-left p-2 font-medium">IP</th>
                      <th className="text-left p-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {abuseLog.map((log, i) => (
                      <tr key={log.id || i} className="hover:bg-slate-50">
                        <td className="p-2 whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleString("en-IN") : "-"}</td>
                        <td className="p-2">
                          <Badge variant="destructive" className="text-[10px]">{log.event_type}</Badge>
                        </td>
                        <td className="p-2 font-mono truncate max-w-[120px]">{log.user_id?.slice(0, 8)}...</td>
                        <td className="p-2 font-mono">{log.ip_address}</td>
                        <td className="p-2 truncate max-w-[200px]">{log.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
